import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { Material, WorkbenchMaterial, CombinationResult, Order, Payout, ServingDish, ShopState } from '../types';
import { CONSTANTS } from '../data.js';
import { Howl, Howler } from 'howler';
import { ASSETS } from '../assetManifest.js';

type ChildrenProps = { children: React.ReactNode };

/* Game State Context */
type GameMode = 'title' | 'loading' | 'playing' | 'splash'; // Added 'splash'
interface GameStateCtx {
  gameState: GameMode;
  setGameState: React.Dispatch<React.SetStateAction<GameMode>>;
  settings: {
    musicVolume: number;
    sfxVolume: number;
  };
  setSettings: React.Dispatch<React.SetStateAction<{
    musicVolume: number;
    sfxVolume: number;
  }>>;
}
const GameStateContext = createContext<GameStateCtx | null>(null);

/* Game Data Context */
interface GameDataCtx {
  MATERIALS: Record<string, Material>;
  RECIPES: Record<string, string>;
  TRIPLE_RECIPES: Record<string, string>;
  RECIPE_METADATA: Record<string, any>;
  POSSIBLE_ORDERS: string[];
  AI_HINTS: Record<string, string>;
  COOKBOOK_CATEGORIES: Record<string, any>;
  SHOP_ITEMS: Record<string, any>;
}
const GameDataContext = createContext<GameDataCtx | null>(null);

/* Inventory Context */
interface InventoryCtx {
  inventory: Material[];
  setInventory: React.Dispatch<React.SetStateAction<Material[]>>;
}
const InventoryContext = createContext<InventoryCtx | null>(null);

/* Workbench Context */
interface WorkbenchCtx {
  workbenchMaterials: WorkbenchMaterial[];
  setWorkbenchMaterials: React.Dispatch<React.SetStateAction<WorkbenchMaterial[]>>;
  addInstance: (id: string, x: number, y: number) => void;
  updateInstancePosition: (instanceId: string, x: number, y: number) => void;
  removeInstance: (instanceId: string) => void;
  clearWorkbench: () => void;
  getNextInstanceId: () => string;
  draggingInstanceId: string | null;
  setDraggingInstanceId: React.Dispatch<React.SetStateAction<string | null>>;
  combinationResult: CombinationResult | null;
  setCombinationResult: React.Dispatch<React.SetStateAction<CombinationResult | null>>;
}
const WorkbenchContext = createContext<WorkbenchCtx | null>(null);

/* Overlay Context */
interface InventoryOverlayState {
  active: boolean;
  itemId: string | null;
  x: number;
  y: number;
}
interface WorkbenchOverlayState {
  active: boolean;
  itemId: string | null;
  instanceId: string | null;
  x: number;
  y: number;
}
interface DragOverlayCtx {
  inventoryOverlay: InventoryOverlayState;
  setInventoryOverlay: React.Dispatch<React.SetStateAction<InventoryOverlayState>>;
  workbenchOverlay: WorkbenchOverlayState;
  setWorkbenchOverlay: React.Dispatch<React.SetStateAction<WorkbenchOverlayState>>;
  inventoryOverlayRef: React.MutableRefObject<HTMLDivElement | null>;
  workbenchOverlayRef: React.MutableRefObject<HTMLDivElement | null>;
  isHoveringInventory: boolean;
  setIsHoveringInventory: React.Dispatch<React.SetStateAction<boolean>>;
}
const DragOverlayContext = createContext<DragOverlayCtx | null>(null);

/* UI Context */
interface UICtx {
  inventoryWidth: number;
  setInventoryWidth: React.Dispatch<React.SetStateAction<number>>;
  showCookbook: boolean;
  setShowCookbook: React.Dispatch<React.SetStateAction<boolean>>;
  searchQuery: string;
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  inventoryPanelRef: React.RefObject<HTMLDivElement>;
  showShop: boolean;
  setShowShop: React.Dispatch<React.SetStateAction<boolean>>;
  isPaused: boolean;
  setIsPaused: React.Dispatch<React.SetStateAction<boolean>>;
}
const UIContext = createContext<UICtx | null>(null);

/* Zoom Context */
interface ZoomCtx {
  zoom: number;
  setZoom: React.Dispatch<React.SetStateAction<number>>;
  scrollRef: React.RefObject<HTMLDivElement>;
  contentRef: React.RefObject<HTMLDivElement>;
}
const ZoomContext = createContext<ZoomCtx | null>(null);

/* Game Loop Context */
interface GameLoopCtx {
  money: number;
  setMoney: React.Dispatch<React.SetStateAction<number>>;
  orders: Order[];
  payoutToast: Payout | null;
  setPayoutToast: React.Dispatch<React.SetStateAction<Payout | null>>;
  addToPantry: (materialId: string) => void;
  currentDay: number;
  currentWeek: number;
  servingQueue: ServingDish[];
  servingAreaRef: React.RefObject<HTMLDivElement>;
  addToServingArea: (dishId: string) => void;
  discoveredRecipesCount: number;
  recipesCreatedToday: number;
  isRecipeCreationDay: boolean;
  isCustomerServingDay: boolean;
  hasCompletedFirstPlaythrough: boolean;
  showDayTransition: boolean;
  setShowDayTransition: React.Dispatch<React.SetStateAction<boolean>>;
  advanceDay: () => void;
}
const GameLoopContext = createContext<GameLoopCtx | null>(null);

/* Shop Context */
interface ShopCtx {
  shopState: ShopState;
  purchaseItem: (itemId: string) => boolean;
  unlockPremiumCookbook: () => boolean;
}
const ShopContext = createContext<ShopCtx | null>(null);

function GameDataProvider({ children }: ChildrenProps) {
  // Game State
  const [gameState, setGameState] = useState<GameMode>('splash'); // Initial state is 'splash'
  const [settings, setSettings] = useState({
    musicVolume: CONSTANTS.MENU.DEFAULT_MUSIC_VOLUME,
    sfxVolume: CONSTANTS.MENU.DEFAULT_SFX_VOLUME
  });

  // Game Data (from CONSTANTS)
  const gameData = useMemo<GameDataCtx>(() => {
    return {
      MATERIALS: CONSTANTS.MATERIALS,
      RECIPES: CONSTANTS.RECIPES,
      TRIPLE_RECIPES: CONSTANTS.TRIPLE_RECIPES,
      RECIPE_METADATA: CONSTANTS.RECIPE_METADATA,
      POSSIBLE_ORDERS: CONSTANTS.POSSIBLE_ORDERS,
      AI_HINTS: CONSTANTS.AI_HINTS,
      COOKBOOK_CATEGORIES: CONSTANTS.COOKBOOK_CATEGORIES,
      SHOP_ITEMS: CONSTANTS.SHOP_ITEMS,
    };
  }, []);

  const gameStateContextValue = useMemo(() => ({ gameState, setGameState, settings, setSettings }), [gameState, settings]);

  return (
    <GameStateContext.Provider value={gameStateContextValue}>
      <GameDataContext.Provider value={gameData}>
        {children}
      </GameDataContext.Provider>
    </GameStateContext.Provider>
  );
}

function CoreProviders({ children }: ChildrenProps) {
  const { gameState } = useGameStateContext();
  const { isPaused } = useUIContext(); // Note: isPaused is from UIContext now
  const gameData = useGameDataContext();

  // Inventory
  const [inventory, setInventory] = useState<Material[]>([]);

  // Workbench
  const [workbenchMaterials, setWorkbenchMaterials] = useState<WorkbenchMaterial[]>([]);
  const instanceIdCounter = useRef(0);
  const [draggingInstanceId, setDraggingInstanceId] = useState<string | null>(null);
  const [combinationResult, setCombinationResult] = useState<CombinationResult | null>(null);

  const getNextInstanceId = useCallback(() => `inst-${instanceIdCounter.current++}`, []);
  const addInstance = useCallback((id: string, x: number, y: number) => {
    setWorkbenchMaterials(prev => [...prev, { id, x, y, instanceId: getNextInstanceId() }]);
  }, [getNextInstanceId]);
  const updateInstancePosition = useCallback((instanceId: string, x: number, y: number) => {
    setWorkbenchMaterials(prev => prev.map(it => it.instanceId === instanceId ? { ...it, x, y } : it));
  }, []);
  const removeInstance = useCallback((instanceId: string) => {
    setWorkbenchMaterials(prev => prev.filter(it => it.instanceId !== instanceId));
  }, []);
  const clearWorkbench = useCallback(() => {
    setWorkbenchMaterials([]);
  }, []);

  // Overlays
  const [inventoryOverlay, setInventoryOverlay] = useState<InventoryOverlayState>({
    active: false, itemId: null, x: 0, y: 0
  });
  const [workbenchOverlay, setWorkbenchOverlay] = useState<WorkbenchOverlayState>({
    active: false, itemId: null, instanceId: null, x: 0, y: 0
  });
  const [isHoveringInventory, setIsHoveringInventory] = useState<boolean>(false);
  const inventoryOverlayRef = useRef<HTMLDivElement | null>(null);
  const workbenchOverlayRef = useRef<HTMLDivElement | null>(null);

  // Zoom
  const [zoom, setZoom] = useState(1);
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Game Loop - Weekly Cycle System
  const [money, setMoney] = useState<number>(0);
  const [orders, setOrders] = useState<Order[]>([]);
  const [pantry, setPantry] = useState<Material[]>([]);
  const [payoutToast, setPayoutToast] = useState<Payout | null>(null);
  const [currentDay, setCurrentDay] = useState<number>(1);
  const [currentWeek, setCurrentWeek] = useState<number>(1);
  const [ordersGeneratedToday, setOrdersGeneratedToday] = useState<number>(0);
  const [ordersCompletedToday, setOrdersCompletedToday] = useState<number>(0);
  const [recipesCreatedToday, setRecipesCreatedToday] = useState<number>(0);
  const [hasCompletedFirstPlaythrough, setHasCompletedFirstPlaythrough] = useState<boolean>(false);
  const [showDayTransition, setShowDayTransition] = useState<boolean>(false);

  // Order timing refs (use refs to avoid effect restarts)
  const firstOrderTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const orderTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const orderCleanupTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Serving Area
  const [servingQueue, setServingQueue] = useState<ServingDish[]>([]);
  const servingAreaRef = useRef<HTMLDivElement>(null);
  const servingIdCounter = useRef(0);

  // Shop State
  const [shopState, setShopState] = useState<ShopState>({
    ownedItems: new Set<string>(),
    premiumCookbookUnlocked: false
  });

  // Keep a ref of ordersGeneratedToday to read latest value inside timers
  const ordersGeneratedTodayRef = useRef<number>(ordersGeneratedToday);
  useEffect(() => {
    ordersGeneratedTodayRef.current = ordersGeneratedToday;
  }, [ordersGeneratedToday]);

  // Create a set of all recipe IDs for efficient lookup
  const recipeIds = useMemo(() => new Set([...Object.values(gameData.RECIPES), ...Object.values(gameData.TRIPLE_RECIPES)]), [gameData]);

  // Save progress whenever key state changes during career mode
  useEffect(() => {
    if (gameState === 'playing') {
      const discoveredRecipes = inventory
        .filter(item => item.discovered && recipeIds.has(item.id))
        .map(item => item.id);
      
      const progress = {
        money,
        currentDay,
        currentWeek,
        hasCompletedFirstPlaythrough,
        discoveredRecipes,
        shopState: {
          ownedItems: Array.from(shopState.ownedItems),
          premiumCookbookUnlocked: shopState.premiumCookbookUnlocked
        }
      };
      
      try {
        localStorage.setItem('yumchemy_career_progress', JSON.stringify(progress));
      } catch (e) {
        console.error('Failed to save career progress', e);
      }
    }
  }, [gameState, money, currentDay, currentWeek, hasCompletedFirstPlaythrough, inventory, shopState, recipeIds]);

  // Determine day type based on weekly cycle
  const isRecipeCreationDay = useMemo(() => {
    return CONSTANTS.WEEKLY_CYCLE.RECIPE_CREATION_DAYS.includes(currentDay);
  }, [currentDay]);

  const isCustomerServingDay = useMemo(() => {
    return CONSTANTS.WEEKLY_CYCLE.CUSTOMER_SERVING_DAYS.includes(currentDay);
  }, [currentDay]);

  // Calculate discovered recipes count
  const discoveredRecipesCount = useMemo(() => {
    return inventory.filter(item => item.discovered && recipeIds.has(item.id)).length;
  }, [inventory, recipeIds]);

  const getRecipeIngredients = useCallback((recipeId: string): string[] => {
    for (const [combo, resultId] of Object.entries(gameData.RECIPES)) {
      if (resultId === recipeId) return combo.split('+');
    }
    for (const [combo, resultId] of Object.entries(gameData.TRIPLE_RECIPES)) {
      if (resultId === recipeId) return combo.split('+');
    }
    return [];
  }, [gameData]);

  const addToPantry = useCallback((materialId: string) => {
    const material = gameData.MATERIALS[materialId];
    if (material) {
      setPantry(prev => [...prev, material]);
    }
  }, [gameData.MATERIALS]);

  const addToServingArea = useCallback((dishId: string) => {
    // Check if this dish matches any pending orders
    const matchingOrder = orders.find(order => order.recipeId === dishId);
    
    if (matchingOrder) {
      // Calculate reward for completing the order
      const material = gameData.MATERIALS[dishId];
      if (material) {
        const ingredients = getRecipeIngredients(dishId);
        let value = CONSTANTS.SERVING_AREA.BASE_ORDER_VALUE + (ingredients.length * CONSTANTS.SERVING_AREA.INGREDIENT_BONUS);
        
        const metadata = gameData.RECIPE_METADATA[dishId];
        if (metadata && ['gourmet', 'exotic', 'magical'].includes(metadata.category)) {
          value *= CONSTANTS.SERVING_AREA.PREMIUM_MULTIPLIER;
        }
        
        // Immediate coin reward for completing order
        setMoney(prev => prev + value);
        
        // Remove the completed order
        setOrders(prev => prev.filter(o => o.id !== matchingOrder.id));
        
        // Track completion for day progression
        if (gameState === 'playing') {
          setOrdersCompletedToday(prev => prev + 1);
        }
        
        // Show payout notification
        const newPayout: Payout = {
          amount: value,
          itemsSold: [{ ...material, count: 1 }],
        };
        setPayoutToast(newPayout);
        setTimeout(() => setPayoutToast(null), CONSTANTS.SERVING_AREA.PAYOUT_DISPLAY_TIME);
      }
    } else {
      // No matching order - add to serving queue for waiter pickup (existing behavior)
      const newDish: ServingDish = {
        id: servingIdCounter.current++,
        dishId,
        timestamp: Date.now()
      };
      setServingQueue(prev => [...prev, newDish]);
    }
  }, [orders, gameData.MATERIALS, gameData.RECIPE_METADATA, getRecipeIngredients, gameState]);

  // Shop functions
  const purchaseItem = useCallback((itemId: string) => {
    const shopItem = CONSTANTS.SHOP_ITEMS[itemId];
    if (!shopItem || money < shopItem.cost || shopState.ownedItems.has(itemId)) {
      return false;
    }

    setMoney(prev => prev - shopItem.cost);
    setShopState(prev => ({
      ...prev,
      ownedItems: new Set([...prev.ownedItems, itemId])
    }));

    const material = gameData.MATERIALS[itemId];
    if (material) {
      setInventory(prev => {
        const existing = prev.find(item => item.id === itemId);
        if (existing) {
          return prev.map(item =>
            item.id === itemId ? { ...item, discovered: true } : item
          );
        } else {
          return [...prev, { ...material, discovered: true }];
        }
      });
    }

    return true;
  }, [money, shopState.ownedItems, gameData.MATERIALS]);

  const unlockPremiumCookbook = useCallback(() => {
    if (money < CONSTANTS.SHOP.PREMIUM_COOKBOOK_COST || 
        currentDay < CONSTANTS.SHOP.PREMIUM_COOKBOOK_UNLOCK_DAY ||
        shopState.premiumCookbookUnlocked) {
      return false;
    }

    setMoney(prev => prev - CONSTANTS.SHOP.PREMIUM_COOKBOOK_COST);
    setShopState(prev => ({
      ...prev,
      premiumCookbookUnlocked: true
    }));

    return true;
  }, [money, currentDay, shopState.premiumCookbookUnlocked]);

  // Initialize Career Mode state/inventory from saved progress on every entry into Career
  const initializeCareerFromStorage = useCallback(() => {
    try {
      // Always start with a clean workbench and inventory for Career
      setWorkbenchMaterials([]);
      setInventory([]);

      const savedProgress = localStorage.getItem('yumchemy_career_progress');
      if (savedProgress) {
        const progress = JSON.parse(savedProgress);

        setMoney(progress.money ?? 0);
        setCurrentDay(progress.currentDay ?? 1);
        setCurrentWeek(progress.currentWeek ?? 1);
        setHasCompletedFirstPlaythrough(!!progress.hasCompletedFirstPlaythrough);

        const baseInventory: Material[] = CONSTANTS.INITIAL_MATERIALS.map((id: string) => {
          const base = gameData.MATERIALS[id];
          if (!base) {
            throw new Error(`INITIAL_MATERIALS contains unknown material id: ${id}`);
          }
          return { ...base, discovered: true };
        });

        let discoveredRecipeItems: Material[] = [];
        if (progress.discoveredRecipes && Array.isArray(progress.discoveredRecipes)) {
          discoveredRecipeItems = progress.discoveredRecipes
            .map((recipeId: string) => {
              const mat = gameData.MATERIALS[recipeId];
              if (!mat) return null;
              return { ...mat, discovered: true } as Material;
            })
            .filter(Boolean) as Material[];
        }

        setInventory([...baseInventory, ...discoveredRecipeItems]);

        if (progress.shopState) {
          setShopState({
            ownedItems: new Set(progress.shopState.ownedItems || []),
            premiumCookbookUnlocked: !!progress.shopState.premiumCookbookUnlocked
          });
        } else {
          setShopState({
            ownedItems: new Set(),
            premiumCookbookUnlocked: false
          });
        }
      } else {
        // No saved progress - initialize fresh Career state
        setMoney(0);
        setCurrentDay(1);
        setCurrentWeek(1);
        setHasCompletedFirstPlaythrough(false);
        setShopState({
          ownedItems: new Set(),
          premiumCookbookUnlocked: false
        });

        const initialMaterials: Material[] = CONSTANTS.INITIAL_MATERIALS.map((id: string) => {
          const base = gameData.MATERIALS[id];
          if (!base) {
            throw new Error(`INITIAL_MATERIALS contains unknown material id: ${id}`);
          }
          return { ...base, discovered: true };
        });
        setInventory(initialMaterials);
      }
    } catch (e) {
      console.error('Failed to load career progress', e);
      // Fallback to fresh start
      setMoney(0);
      setCurrentDay(1);
      setCurrentWeek(1);
      setHasCompletedFirstPlaythrough(false);
      setShopState({
        ownedItems: new Set(),
        premiumCookbookUnlocked: false
      });

      const initialMaterials: Material[] = CONSTANTS.INITIAL_MATERIALS.map((id: string) => {
        const base = gameData.MATERIALS[id];
        if (!base) {
          throw new Error(`INITIAL_MATERIALS contains unknown material id: ${id}`);
        }
        return { ...base, discovered: true };
      });
      setInventory(initialMaterials);
    }
  }, [gameData.MATERIALS]);

  useEffect(() => {
    if (gameState === 'playing') {
      initializeCareerFromStorage();
    }
  }, [gameState, initializeCareerFromStorage]);

  // Clear timers when leaving playing mode or when paused
  useEffect(() => {
    if (gameState !== 'playing' || isPaused) {
      if (firstOrderTimerRef.current) {
        clearTimeout(firstOrderTimerRef.current);
        firstOrderTimerRef.current = null;
      }
      if (orderTimerRef.current) {
        clearTimeout(orderTimerRef.current);
        orderTimerRef.current = null;
      }
      if (orderCleanupTimerRef.current) {
        clearInterval(orderCleanupTimerRef.current);
        orderCleanupTimerRef.current = null;
      }
    }
  }, [gameState, isPaused]);

  // Order cleanup system - remove expired orders
  useEffect(() => {
    if (gameState !== 'playing') return;
    if (isPaused) return;

    orderCleanupTimerRef.current = setInterval(() => {
      const now = Date.now();
      setOrders(currentOrders => {
        return currentOrders.filter(order => {
          if (order.expiresAt && now >= order.expiresAt) {
            return false; // Remove expired order
          }
          return true; // Keep non-expired order
        });
      });
    }, CONSTANTS.ORDER_SYSTEM.CLEANUP_INTERVAL_MS);

    return () => {
      if (orderCleanupTimerRef.current) {
        clearInterval(orderCleanupTimerRef.current);
        orderCleanupTimerRef.current = null;
      }
    };
  }, [gameState, isPaused]);

  // Order generation system for career mode (fixed to avoid resets)
  const sessionKeyRef = useRef<string>('');
  useEffect(() => {
    if (gameState !== 'playing' || isPaused || !isCustomerServingDay) {
      return;
    }

    // On first day, wait for tutorial completion if it's the first playthrough
    if (currentDay === 1 && currentWeek === 1 && !hasCompletedFirstPlaythrough && 
        discoveredRecipesCount < CONSTANTS.WEEKLY_CYCLE.TUTORIAL_RECIPES_TO_DISCOVER) {
      return;
    }

    const sessionKey = `${currentWeek}-${currentDay}`;
    const isNewSession = sessionKeyRef.current !== sessionKey;

    if (isNewSession) {
      // New serving day session: reset orders and counters once
      setOrders([]);
      setOrdersGeneratedToday(0);
      ordersGeneratedTodayRef.current = 0;

      // Clear any lingering timers from previous session
      if (firstOrderTimerRef.current) {
        clearTimeout(firstOrderTimerRef.current);
        firstOrderTimerRef.current = null;
      }
      if (orderTimerRef.current) {
        clearTimeout(orderTimerRef.current);
        orderTimerRef.current = null;
      }

      sessionKeyRef.current = sessionKey;
    }

    // Helper to generate a single order
    const generateOrder = () => {
      // Respect daily order cap
      if (ordersGeneratedTodayRef.current >= CONSTANTS.WEEKLY_CYCLE.ORDERS_PER_SERVING_DAY) {
        return;
      }

      // Compute available orders from currently discovered recipes
      const allRecipeIds = new Set([...Object.values(gameData.RECIPES), ...Object.values(gameData.TRIPLE_RECIPES)]);
      const discoveredRecipeIds = inventory
        .filter(item => item.discovered && allRecipeIds.has(item.id))
        .map(item => item.id);

      let availableOrders = discoveredRecipeIds;
      if (!shopState.premiumCookbookUnlocked) {
        availableOrders = availableOrders.filter(orderId => {
          const metadata = gameData.RECIPE_METADATA[orderId];
          return metadata && !['gourmet', 'exotic', 'magical', 'custom'].includes(metadata.category);
        });
      }

      if (availableOrders.length === 0) {
        return;
      }

      const randomRecipeId = availableOrders[Math.floor(Math.random() * availableOrders.length)];
      const now = Date.now();
      const newOrder: Order = {
        id: Date.now() + Math.random(),
        recipeId: randomRecipeId,
        createdAt: now,
        expiresAt: now + CONSTANTS.ORDER_SYSTEM.TIMEOUT_MS,
      };

      setOrders(currentOrders => [...currentOrders, newOrder]);
      setOrdersGeneratedToday(prev => prev + 1);
      ordersGeneratedTodayRef.current = ordersGeneratedTodayRef.current + 1;
    };

    // Schedule next order repeatedly until cap is reached
    const scheduleNextOrder = () => {
      if (ordersGeneratedTodayRef.current >= CONSTANTS.WEEKLY_CYCLE.ORDERS_PER_SERVING_DAY) {
        return;
      }
      const minInterval = CONSTANTS.WEEKLY_CYCLE.ORDER_INTERVAL_MIN_MS;
      const maxInterval = CONSTANTS.WEEKLY_CYCLE.ORDER_INTERVAL_MAX_MS;
      const randomInterval = minInterval + Math.random() * (maxInterval - minInterval);

      orderTimerRef.current = setTimeout(() => {
        generateOrder();
        scheduleNextOrder();
      }, randomInterval);
    };

    // Start first order timer if not already started
    if (!firstOrderTimerRef.current) {
      firstOrderTimerRef.current = setTimeout(() => {
        generateOrder();
        scheduleNextOrder();
      }, CONSTANTS.WEEKLY_CYCLE.FIRST_ORDER_DELAY_MS);
    }

    // Cleanup is handled by the pause/leave effect; no cleanup here to avoid cancelling running timers on minor deps changes
  }, [
    gameState,
    isPaused,
    isCustomerServingDay,
    currentDay,
    currentWeek,
    discoveredRecipesCount,
    hasCompletedFirstPlaythrough,
    shopState.premiumCookbookUnlocked,
    gameData,
    inventory
  ]);

  // Day advancement function for dev console and game logic
  const advanceDay = useCallback(() => {
    // Show day transition notification
    setShowDayTransition(true);
    setTimeout(() => {
      setShowDayTransition(false);
    }, CONSTANTS.WEEKLY_CYCLE.DAY_TRANSITION_DISPLAY_MS);

    // Clear workbench when day transitions
    setWorkbenchMaterials([]);

    setCurrentDay(prev => {
      const nextDay = prev + 1;
      if (nextDay > CONSTANTS.WEEKLY_CYCLE.DAYS_PER_WEEK) {
        setCurrentWeek(w => w + 1);
        setRecipesCreatedToday(0);
        return 1;
      }
      setRecipesCreatedToday(0);
      return nextDay;
    });
    setOrdersGeneratedToday(0);
    setOrdersCompletedToday(0);
    setOrders([]);
    
    // Mark first playthrough as completed after first day
    if (currentDay === 1 && currentWeek === 1 && !hasCompletedFirstPlaythrough) {
      setHasCompletedFirstPlaythrough(true);
    }
  }, [currentDay, currentWeek, hasCompletedFirstPlaythrough, setWorkbenchMaterials, setShowDayTransition]);

  // Weekly cycle day transition system (only when playing)
  useEffect(() => {
    if (gameState !== 'playing' || isPaused) return;

    let shouldTransition = false;
    
    if (currentDay === 1 && currentWeek === 1 && !hasCompletedFirstPlaythrough) {
      // First day tutorial completion
      if (discoveredRecipesCount >= CONSTANTS.WEEKLY_CYCLE.TUTORIAL_RECIPES_TO_DISCOVER) {
        shouldTransition = true;
      }
    } else if (isCustomerServingDay) {
      // Customer serving days: transition when orders completed
      if (ordersCompletedToday >= CONSTANTS.WEEKLY_CYCLE.ORDERS_PER_SERVING_DAY) {
        shouldTransition = true;
      }
    } else if (isRecipeCreationDay) {
      // Recipe creation days: transition after time or when max recipes created
      const timeBasedTransition = setTimeout(() => {
        advanceDay();
      }, CONSTANTS.WEEKLY_CYCLE.ORDER_INTERVAL_MIN_MS * 8); // Give time for recipe creation

      return () => clearTimeout(timeBasedTransition);
    }

    if (shouldTransition) {
      const dayTransitionTimeout = setTimeout(() => {
        advanceDay();
      }, 3000);

      return () => clearTimeout(dayTransitionTimeout);
    }
  }, [ordersCompletedToday, gameState, isPaused, currentDay, currentWeek, discoveredRecipesCount, hasCompletedFirstPlaythrough, isCustomerServingDay, isRecipeCreationDay, advanceDay]);

  // Waiter pickup system for serving area (only when playing)
  useEffect(() => {
    if (gameState !== 'playing' || isPaused) return;

    const waiterInterval = setInterval(() => {
      setServingQueue(currentQueue => {
        const now = Date.now();
        const readyToServe = currentQueue.filter(dish => 
          now - dish.timestamp >= CONSTANTS.SERVING_AREA.WAITER_PICKUP_TIME
        );
        
        if (readyToServe.length === 0) return currentQueue;

        let totalPayout = 0;
        const soldItemsTally = new Map<string, { id: string; name: string; icon: string; count: number }>();

        readyToServe.forEach(dish => {
          const material = gameData.MATERIALS[dish.dishId];
          if (material) {
            const ingredients = getRecipeIngredients(dish.dishId);
            let value = CONSTANTS.GAME_SETTINGS.BASE_RECIPE_VALUE + (ingredients.length * CONSTANTS.GAME_SETTINGS.TIP_BONUS_PER_INGREDIENT);
            
            const metadata = gameData.RECIPE_METADATA[dish.dishId];
            if (metadata && ['gourmet', 'exotic', 'magical'].includes(metadata.category)) {
              value *= 2;
            }
            
            totalPayout += value;

            if (soldItemsTally.has(dish.dishId)) {
              soldItemsTally.get(dish.dishId)!.count++;
            } else {
              soldItemsTally.set(dish.dishId, { ...material, count: 1 });
            }
          }
        });

        if (totalPayout > 0) {
          setMoney(prev => prev + totalPayout);
          
          const newPayout: Payout = {
            amount: totalPayout,
            itemsSold: Array.from(soldItemsTally.values()),
          };
          setPayoutToast(newPayout);
          setTimeout(() => setPayoutToast(null), 8000);
        }

        return currentQueue.filter(dish => !readyToServe.includes(dish));
      });
    }, CONSTANTS.SERVING_AREA.WAITER_CHECK_INTERVAL);

    return () => clearInterval(waiterInterval);
  }, [gameState, isPaused, gameData, getRecipeIngredients]);

  // Serving loop (pantry) - remove orders when items are served
  useEffect(() => {
    if (gameState !== 'playing' || isPaused) return;

    const servingInterval = setInterval(() => {
      if (pantry.length === 0 || orders.length === 0) return;

      let pantryCopy = [...pantry];
      let ordersCopy = [...orders];
      let totalPayout = 0;
      const soldItemsTally = new Map<string, { id: string; name: string; icon: string; count: number }>();
      const fulfilledOrderIds = new Set<number>();

      const sortedOrders = [...ordersCopy].sort((a, b) => a.createdAt - b.createdAt);

      for (const order of sortedOrders) {
        const itemIndex = pantryCopy.findIndex(item => item.id === order.recipeId);

        if (itemIndex > -1) {
          const soldItem = pantryCopy[itemIndex];
          const ingredients = getRecipeIngredients(soldItem.id);
          let value = CONSTANTS.GAME_SETTINGS.BASE_RECIPE_VALUE + (ingredients.length * CONSTANTS.GAME_SETTINGS.TIP_BONUS_PER_INGREDIENT);
          
          const metadata = gameData.RECIPE_METADATA[soldItem.id];
          if (metadata && ['gourmet', 'exotic', 'magical'].includes(metadata.category)) {
            value *= 2;
          }
          
          totalPayout += value;

          if (soldItemsTally.has(soldItem.id)) {
            soldItemsTally.get(soldItem.id)!.count++;
          } else {
            soldItemsTally.set(soldItem.id, { ...soldItem, count: 1 });
          }

          pantryCopy.splice(itemIndex, 1);
          fulfilledOrderIds.add(order.id);
        }
      }

      if (totalPayout > 0) {
        setMoney(prev => prev + totalPayout);
        setPantry(pantryCopy);
        setOrders(current => current.filter(o => !fulfilledOrderIds.has(o.id)));
        if (gameState === 'playing') {
          setOrdersCompletedToday(prev => prev + fulfilledOrderIds.size);
        }
        
        const newPayout: Payout = {
          amount: totalPayout,
          itemsSold: Array.from(soldItemsTally.values()),
        };
        setPayoutToast(newPayout);
        setTimeout(() => setPayoutToast(null), 8000);
      }
    }, CONSTANTS.GAME_SETTINGS.SERVING_INTERVAL_MS);

    return () => clearInterval(servingInterval);
  }, [pantry, orders, gameState, isPaused, gameData, getRecipeIngredients]);

  // Track recipe creation for daily limits
  const originalSetInventory = setInventory;
  const wrappedSetInventory = useCallback((updater: React.SetStateAction<Material[]>) => {
    if (gameState === 'playing' && isRecipeCreationDay) {
      originalSetInventory(prev => {
        const newInventory = typeof updater === 'function' ? updater(prev) : updater;
        
        // Count newly discovered recipes
        const newlyDiscovered = newInventory.filter(item => 
          item.discovered && 
          recipeIds.has(item.id) && 
          !prev.find(p => p.id === item.id && p.discovered)
        );
        
        if (newlyDiscovered.length > 0) {
          setRecipesCreatedToday(current => {
            const newCount = current + newlyDiscovered.length;
            if (newCount > CONSTANTS.WEEKLY_CYCLE.MAX_RECIPES_PER_CREATION_DAY) {
              // Prevent exceeding daily limit
              return current;
            }
            return newCount;
          });
        }
        
        return newInventory;
      });
    } else {
      originalSetInventory(updater);
    }
  }, [gameState, isRecipeCreationDay, recipeIds, originalSetInventory]);

  const gameLoopContextValue = useMemo(() => ({ 
    money, 
    setMoney,
    orders, 
    payoutToast, 
    setPayoutToast, 
    addToPantry, 
    currentDay, 
    currentWeek,
    servingQueue, 
    servingAreaRef, 
    addToServingArea, 
    discoveredRecipesCount,
    recipesCreatedToday,
    isRecipeCreationDay,
    isCustomerServingDay,
    hasCompletedFirstPlaythrough,
    showDayTransition,
    setShowDayTransition,
    advanceDay
  }), [money, setMoney, orders, payoutToast, addToPantry, currentDay, currentWeek, servingQueue, discoveredRecipesCount, addToServingArea, recipesCreatedToday, isRecipeCreationDay, isCustomerServingDay, hasCompletedFirstPlaythrough, showDayTransition, advanceDay]);
  
  const shopContextValue = useMemo(() => ({ shopState, purchaseItem, unlockPremiumCookbook }), [shopState, purchaseItem, unlockPremiumCookbook]);
  const inventoryContextValue = useMemo(() => ({ inventory, setInventory: wrappedSetInventory }), [inventory, wrappedSetInventory]);
  const workbenchContextValue = useMemo(() => ({ workbenchMaterials, setWorkbenchMaterials, addInstance, updateInstancePosition, removeInstance, clearWorkbench, getNextInstanceId, draggingInstanceId, setDraggingInstanceId, combinationResult, setCombinationResult }), [workbenchMaterials, addInstance, updateInstancePosition, removeInstance, clearWorkbench, getNextInstanceId, draggingInstanceId, combinationResult]);
  const dragOverlayContextValue = useMemo(() => ({ inventoryOverlay, setInventoryOverlay, workbenchOverlay, setWorkbenchOverlay, inventoryOverlayRef, workbenchOverlayRef, isHoveringInventory, setIsHoveringInventory }), [inventoryOverlay, workbenchOverlay, isHoveringInventory]);
  const zoomContextValue = useMemo(() => ({ zoom, setZoom, scrollRef, contentRef }), [zoom]);

  return (
    <GameLoopContext.Provider value={gameLoopContextValue}>
      <ShopContext.Provider value={shopContextValue}>
        <InventoryContext.Provider value={inventoryContextValue}>
          <WorkbenchContext.Provider value={workbenchContextValue}>
            <DragOverlayContext.Provider value={dragOverlayContextValue}>
              <ZoomContext.Provider value={zoomContextValue}>
                {children}
              </ZoomContext.Provider>
            </DragOverlayContext.Provider>
          </WorkbenchContext.Provider>
        </InventoryContext.Provider>
      </ShopContext.Provider>
    </GameLoopContext.Provider>
  );
}

export function GameProviders({ children }: ChildrenProps) {
  return (
    <GameDataProvider>
      <UIProvider>
        <CoreProviders>
          {children}
        </CoreProviders>
      </UIProvider>
    </GameDataProvider>
  );
}

function UIProvider({ children }: ChildrenProps) {
  const { gameState, settings } = useGameStateContext();
  const musicRef = useRef<Howl | null>(null);
  const musicInitializedRef = useRef(false);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Initialize music system
  useEffect(() => {
    const musicAsset = (ASSETS as any)?.music?.main_theme;
    if (!musicAsset || !musicAsset.path) {
      console.warn('Main theme music asset not found in assetManifest.js');
      return;
    }

    // Create the Howl instance
    const music = new Howl({
      src: [musicAsset.path],
      loop: true,
      volume: 0,
      html5: true,
      preload: true,
    });

    musicRef.current = music;

    // Function to attempt music playback
    const attemptPlayback = () => {
      if (!music || musicInitializedRef.current) return;

      try {
        // Resume audio context if suspended
        if (Howler.ctx && Howler.ctx.state === 'suspended') {
          Howler.ctx.resume().then(() => {
            if (!music.playing()) {
              music.play();
              musicInitializedRef.current = true;
            }
          }).catch(() => {
            // Retry after a short delay
            if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = setTimeout(attemptPlayback, 1000);
          });
        } else {
          // Try to play directly
          if (!music.playing()) {
            music.play();
            musicInitializedRef.current = true;
          }
        }
      } catch (error) {
        // Retry after a short delay
        if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = setTimeout(attemptPlayback, 1000);
      }
    };

    // Set up multiple event listeners to catch any user interaction
    const interactionEvents = ['click', 'keydown', 'touchstart', 'pointerdown', 'mousedown'];
    
    const handleInteraction = () => {
      attemptPlayback();
      // Remove listeners after first successful interaction
      interactionEvents.forEach(event => {
        window.removeEventListener(event, handleInteraction);
        document.removeEventListener(event, handleInteraction);
      });
    };

    // Add listeners to both window and document to catch any interaction
    interactionEvents.forEach(event => {
      window.addEventListener(event, handleInteraction, { once: true, passive: true });
      document.addEventListener(event, handleInteraction, { once: true, passive: true });
    });

    // Try to start immediately (may work in some browsers/contexts)
    attemptPlayback();

    // Also try after a short delay
    const initialDelay = setTimeout(attemptPlayback, 100);

    return () => {
      if (music) {
        music.unload();
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      clearTimeout(initialDelay);
      interactionEvents.forEach(event => {
        window.removeEventListener(event, handleInteraction);
        document.removeEventListener(event, handleInteraction);
      });
      musicRef.current = null;
      musicInitializedRef.current = false;
    };
  }, []);

  // Control music volume and ensure it's playing
  useEffect(() => {
    const music = musicRef.current;
    if (!music) return;

    const targetVolume = settings.musicVolume / 100;
    
    // Ensure music is playing
    if (!music.playing() && musicInitializedRef.current) {
      try {
        music.play();
      } catch (error) {
        console.warn('Failed to resume music playback:', error);
      }
    }

    // Update volume with smooth transition
    if (music.volume() !== targetVolume) {
      music.fade(music.volume(), targetVolume, CONSTANTS.MUSIC.VOLUME_FADE_DURATION_MS);
    }
  }, [settings.musicVolume]);

  // Ensure music continues playing when game state changes
  useEffect(() => {
    const music = musicRef.current;
    if (!music || !musicInitializedRef.current) return;

    // Make sure music is still playing after state changes
    if (!music.playing()) {
      try {
        music.play();
      } catch (error) {
        console.warn('Failed to restart music after state change:', error);
      }
    }
  }, [gameState]);

  // UI State
  const [inventoryWidth, setInventoryWidth] = useState<number>(CONSTANTS.INVENTORY_DEFAULT_WIDTH);
  const [showCookbook, setShowCookbook] = useState<boolean>(false);
  const [showShop, setShowShop] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const inventoryPanelRef = useRef<HTMLDivElement>(null);
  const [isPaused, setIsPaused] = useState<boolean>(false);

  const uiContextValue = useMemo(() => ({
    inventoryWidth, setInventoryWidth,
    showCookbook, setShowCookbook,
    searchQuery, setSearchQuery,
    inventoryPanelRef,
    showShop, setShowShop,
    isPaused, setIsPaused,
  }), [inventoryWidth, showCookbook, searchQuery, showShop, isPaused]);

  return (
    <UIContext.Provider value={uiContextValue}>
      {children}
    </UIContext.Provider>
  );
}

/* Hooks to consume contexts */
export function useGameStateContext() {
  const ctx = useContext(GameStateContext);
  if (!ctx) throw new Error('useGameStateContext must be used within GameProviders');
  return ctx;
}
export function useGameDataContext() {
  const ctx = useContext(GameDataContext);
  if (!ctx) throw new Error('useGameDataContext must be used within GameProviders');
  return ctx;
}
export function useInventoryContext() {
  const ctx = useContext(InventoryContext);
  if (!ctx) throw new Error('useInventoryContext must be used within GameProviders');
  return ctx;
}
export function useWorkbenchContext() {
  const ctx = useContext(WorkbenchContext);
  if (!ctx) throw new Error('useWorkbenchContext must be used within GameProviders');
  return ctx;
}
export function useDragOverlayContext() {
  const ctx = useContext(DragOverlayContext);
  if (!ctx) throw new Error('useDragOverlayContext must be used within GameProviders');
  return ctx;
}
export function useUIContext() {
  const ctx = useContext(UIContext);
  if (!ctx) throw new Error('useUIContext must be used within GameProviders');
  return ctx;
}
export function useZoomContext() {
  const ctx = useContext(ZoomContext);
  if (!ctx) throw new Error('useZoomContext must be used within GameProviders');
  return ctx;
}
export function useGameLoopContext() {
  const ctx = useContext(GameLoopContext);
  if (!ctx) throw new Error('useGameLoopContext must be used within GameProviders');
  return ctx;
}
export function useShopContext() {
  const ctx = useContext(ShopContext);
  if (!ctx) throw new Error('useShopContext must be used within GameProviders');
  return ctx;
}
